import DOMPurify from 'dompurify';

/**
 * Align with public RichContent: allow Quill colors + simple tables.
 */
const TABLE_TAGS = ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col'];
const TABLE_TAG_SET = new Set(TABLE_TAGS.map((t) => t.toUpperCase()));

const TABLE_STYLE_PROPS = new Set([
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-collapse',
  'border-spacing',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'width',
  'height',
  'max-width',
  'min-width',
  'min-height',
  'text-align',
  'vertical-align',
  'background-color',
  'background',
  'color',
  'font-weight',
  'font-size',
  'white-space',
  'box-sizing'
]);

function safeStyleValue(val) {
  const s = String(val || '').trim();
  if (!s || /url\s*\(/i.test(s) || /expression\s*\(/i.test(s) || /@import/i.test(s)) return null;
  if (!/^[\w#().%,\s+/:;'"=-]+$/.test(s)) return null;
  return s;
}

let styleHookInstalled = false;
if (!styleHookInstalled) {
  styleHookInstalled = true;
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName !== 'style' || !node?.tagName) return;
    const v = String(data.attrValue || '').trim();
    if (!v) {
      data.keepAttr = false;
      return;
    }
    const parts = v.split(';').map((s) => s.trim()).filter(Boolean);
    const allowed = [];
    const isTableEl = TABLE_TAG_SET.has(node.tagName);
    const isImg = node.tagName.toUpperCase() === 'IMG';
    for (const p of parts) {
      const m = /^([\w-]+)\s*:\s*(.+)$/i.exec(p);
      if (!m) continue;
      const prop = m[1].toLowerCase();
      const rawVal = m[2].trim();
      const val = safeStyleValue(rawVal);
      if (val == null) continue;
      if (isTableEl) {
        if (!TABLE_STYLE_PROPS.has(prop)) continue;
        allowed.push(`${prop}: ${val}`);
        continue;
      }
      if (isImg) {
        const allowedImgProps = new Set([
          'width', 'height', 'max-width', 'min-width', 'max-height', 'min-height',
          'float', 'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
          'display', 'aspect-ratio', 'object-fit'
        ]);
        if (allowedImgProps.has(prop)) {
          allowed.push(`${prop}: ${val}`);
          continue;
        }
      }
      if (prop !== 'color' && prop !== 'background-color') continue;
      if (!/^[#\w().%,\s+-]+$/.test(val)) continue;
      allowed.push(`${prop}: ${val}`);
    }
    if (!allowed.length) {
      data.keepAttr = false;
      return;
    }
    data.attrValue = allowed.join('; ');
  });
}

export function sanitizeRichHtml(html) {
  const s = String(html || '').trim();
  if (!s) return '';
  return DOMPurify.sanitize(s, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'span', 'div',
      'blockquote', 'pre', 'code', 'hr',
      'img',
      ...TABLE_TAGS
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'src', 'alt', 'class', 'style',
      'colspan', 'rowspan', 'width', 'height'
    ]
  });
}
